import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Copy, CheckCircle, Edit2, Trash2, Building2, FileStack, Shield, Zap, AlertTriangle, Wand2 } from 'lucide-react';
import { OpsLayout } from '@/components/ops/OpsLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { Tables } from '@/integrations/supabase/types';

type Payer = Tables<'payers'>;
type Plan = Tables<'plans'>;
type Ruleset = Tables<'rulesets'>;

// Rule confidence labels and colors
const RULE_CONFIDENCE = {
  TEMPLATE: { label: 'Template', color: 'bg-slate-500/10 text-slate-600 border-slate-300' },
  VERIFIED: { label: 'Verified', color: 'bg-emerald-500/10 text-emerald-600 border-emerald-300' },
  LEARNED: { label: 'Learned', color: 'bg-purple-500/10 text-purple-600 border-purple-300' },
} as const;

const RULESET_STATUS = {
  DRAFT: { label: 'Draft', color: 'bg-amber-500/10 text-amber-600' },
  ACTIVE: { label: 'Active', color: 'bg-emerald-500/10 text-emerald-600' },
  ARCHIVED: { label: 'Archived', color: 'bg-slate-500/10 text-slate-500' },
} as const;

export default function PayerPlansAdmin() {
  const [payers, setPayers] = useState<Payer[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [rulesets, setRulesets] = useState<Ruleset[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPayer, setSelectedPayer] = useState<string>('all');
  
  // Dialog states
  const [isAddPlanOpen, setIsAddPlanOpen] = useState(false);
  const [isCloneRulesetOpen, setIsCloneRulesetOpen] = useState(false);
  const [selectedRulesetToClone, setSelectedRulesetToClone] = useState<Ruleset | null>(null);
  
  // Form states
  const [newPlanName, setNewPlanName] = useState('');
  const [newPlanPayerId, setNewPlanPayerId] = useState('');
  const [newPlanIsDefault, setNewPlanIsDefault] = useState(false);
  const [cloneRulesetName, setCloneRulesetName] = useState('');
  const [cloneRulesetPlanId, setCloneRulesetPlanId] = useState('');
  
  const { toast } = useToast();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [payersRes, plansRes, rulesetsRes] = await Promise.all([
        supabase.from('payers').select('*').order('name'),
        supabase.from('plans').select('*').order('plan_name'),
        supabase.from('rulesets').select('*').order('name'),
      ]);
      
      if (payersRes.data) setPayers(payersRes.data);
      if (plansRes.data) setPlans(plansRes.data);
      if (rulesetsRes.data) setRulesets(rulesetsRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getPayerName = (payerId: string) => {
    const payer = payers.find(p => p.id === payerId);
    return payer?.name || 'Unknown';
  };

  const getPayerType = (payerId: string) => {
    const payer = payers.find(p => p.id === payerId);
    return payer?.payer_type || 'COMMERCIAL';
  };

  const filteredPlans = selectedPayer === 'all' 
    ? plans 
    : plans.filter(p => p.payer_id === selectedPayer);

  const filteredRulesets = selectedPayer === 'all'
    ? rulesets
    : rulesets.filter(r => r.payer_id === selectedPayer);

  const handleAddPlan = async () => {
    if (!newPlanName || !newPlanPayerId) {
      toast({ title: 'Error', description: 'Please fill in all required fields.', variant: 'destructive' });
      return;
    }

    const { error } = await supabase.from('plans').insert({
      plan_name: newPlanName,
      payer_id: newPlanPayerId,
      is_default_plan: newPlanIsDefault,
      state: 'OH',
    });

    if (error) {
      toast({ title: 'Error', description: 'Failed to create plan.', variant: 'destructive' });
    } else {
      toast({ title: 'Plan created', description: `${newPlanName} has been added.` });
      setIsAddPlanOpen(false);
      setNewPlanName('');
      setNewPlanPayerId('');
      setNewPlanIsDefault(false);
      fetchData();
    }
  };

  const handleCloneRuleset = async () => {
    if (!selectedRulesetToClone || !cloneRulesetName) {
      toast({ title: 'Error', description: 'Please fill in all required fields.', variant: 'destructive' });
      return;
    }

    // Create new ruleset as a clone
    const { data: newRuleset, error: rulesetError } = await supabase.from('rulesets').insert({
      name: cloneRulesetName,
      payer_id: selectedRulesetToClone.payer_id,
      plan_id: cloneRulesetPlanId || null,
      description: `Cloned from ${selectedRulesetToClone.name}`,
      status: 'DRAFT',
      version: 1,
      jurisdiction: selectedRulesetToClone.jurisdiction,
      effective_start_date: new Date().toISOString().split('T')[0],
    }).select().single();

    if (rulesetError || !newRuleset) {
      toast({ title: 'Error', description: 'Failed to clone ruleset.', variant: 'destructive' });
      return;
    }

    // Clone all rules from the original ruleset
    const { data: originalRules } = await supabase
      .from('rules')
      .select('*')
      .eq('ruleset_id', selectedRulesetToClone.id);

    if (originalRules && originalRules.length > 0) {
      const clonedRules = originalRules.map(rule => ({
        ruleset_id: newRuleset.id,
        rule_type: rule.rule_type,
        match_criteria: rule.match_criteria,
        decision: rule.decision,
        priority: rule.priority,
        confidence: 'TEMPLATE' as const,
        notes: `Cloned from original rule`,
        is_active: true,
      }));

      await supabase.from('rules').insert(clonedRules);
    }

    toast({ title: 'Ruleset cloned', description: `${cloneRulesetName} created with ${originalRules?.length || 0} rules.` });
    setIsCloneRulesetOpen(false);
    setSelectedRulesetToClone(null);
    setCloneRulesetName('');
    setCloneRulesetPlanId('');
    fetchData();
  };

  const handleActivateRuleset = async (ruleset: Ruleset) => {
    // Deactivate other active rulesets for this payer/plan combo
    if (ruleset.plan_id) {
      await supabase
        .from('rulesets')
        .update({ status: 'ARCHIVED' })
        .eq('payer_id', ruleset.payer_id)
        .eq('plan_id', ruleset.plan_id)
        .eq('status', 'ACTIVE');
    } else {
      await supabase
        .from('rulesets')
        .update({ status: 'ARCHIVED' })
        .eq('payer_id', ruleset.payer_id)
        .is('plan_id', null)
        .eq('status', 'ACTIVE');
    }

    // Activate the selected ruleset
    const { error } = await supabase
      .from('rulesets')
      .update({ status: 'ACTIVE' })
      .eq('id', ruleset.id);

    if (error) {
      toast({ title: 'Error', description: 'Failed to activate ruleset.', variant: 'destructive' });
    } else {
      toast({ title: 'Ruleset activated', description: `${ruleset.name} is now active.` });
      fetchData();
    }
  };

  const handleDeletePlan = async (planId: string) => {
    const { error } = await supabase.from('plans').delete().eq('id', planId);
    if (error) {
      toast({ title: 'Error', description: 'Failed to delete plan.', variant: 'destructive' });
    } else {
      toast({ title: 'Plan deleted' });
      fetchData();
    }
  };

  return (
    <OpsLayout>
      <div className="w-full max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Payer & Plan Management</h1>
            <p className="text-muted-foreground mt-2">
              Manage payers, plans, and ruleset versions
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <Link to="/admin/payer-setup">
                <Wand2 className="h-4 w-4 mr-2" />
                Setup Wizard
              </Link>
            </Button>
            <Dialog open={isAddPlanOpen} onOpenChange={setIsAddPlanOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Plan
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New Plan</DialogTitle>
                  <DialogDescription>
                    Create a new plan under an existing payer.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Payer</Label>
                    <Select value={newPlanPayerId} onValueChange={setNewPlanPayerId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select payer" />
                      </SelectTrigger>
                      <SelectContent>
                        {payers.map(payer => (
                          <SelectItem key={payer.id} value={payer.id}>
                            {payer.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Plan Name</Label>
                    <Input
                      placeholder="e.g., Anthem HMO"
                      value={newPlanName}
                      onChange={(e) => setNewPlanName(e.target.value)}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="isDefault"
                      checked={newPlanIsDefault}
                      onCheckedChange={(checked) => setNewPlanIsDefault(!!checked)}
                    />
                    <Label htmlFor="isDefault">Set as default plan</Label>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsAddPlanOpen(false)}>Cancel</Button>
                  <Button onClick={handleAddPlan}>Create Plan</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-2xl">{payers.length}</CardTitle>
              <CardDescription>Payers</CardDescription>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-2xl">{plans.length}</CardTitle>
              <CardDescription>Plans</CardDescription>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-2xl">{rulesets.filter(r => r.status === 'ACTIVE').length}</CardTitle>
              <CardDescription>Active Rulesets</CardDescription>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-2xl">{rulesets.filter(r => r.status === 'DRAFT').length}</CardTitle>
              <CardDescription>Draft Rulesets</CardDescription>
            </CardHeader>
          </Card>
        </div>

        {/* Filter */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Label>Filter by Payer:</Label>
              <Select value={selectedPayer} onValueChange={setSelectedPayer}>
                <SelectTrigger className="w-[300px]">
                  <SelectValue placeholder="All Payers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Payers</SelectItem>
                  {payers.map(payer => (
                    <SelectItem key={payer.id} value={payer.id}>
                      {payer.name} ({payer.payer_type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="payers" className="space-y-6">
          <TabsList>
            <TabsTrigger value="payers">Payers</TabsTrigger>
            <TabsTrigger value="plans">Plans</TabsTrigger>
            <TabsTrigger value="rulesets">Rulesets</TabsTrigger>
          </TabsList>

          {/* Payers Tab */}
          <TabsContent value="payers" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {payers.map(payer => {
                const payerPlans = plans.filter(p => p.payer_id === payer.id);
                const payerRulesets = rulesets.filter(r => r.payer_id === payer.id);
                const activeRulesets = payerRulesets.filter(r => r.status === 'ACTIVE');
                
                return (
                  <Card key={payer.id} className="relative">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Building2 className="h-4 w-4" />
                            {payer.name}
                          </CardTitle>
                          <CardDescription className="font-mono text-xs mt-1">
                            {payer.payer_id}
                          </CardDescription>
                        </div>
                        <Badge variant="outline" className={
                          payer.payer_type === 'BWC' ? 'bg-orange-500/10 text-orange-600' :
                          payer.payer_type === 'MEDICARE' ? 'bg-blue-500/10 text-blue-600' :
                          'bg-slate-500/10 text-slate-600'
                        }>
                          {payer.payer_type}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Plans</span>
                        <Badge variant="secondary">{payerPlans.length}</Badge>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Active Rulesets</span>
                        <Badge variant="secondary" className="bg-emerald-500/10 text-emerald-600">
                          {activeRulesets.length}
                        </Badge>
                      </div>
                      {payer.epa_enabled && (
                        <div className="flex items-center gap-2 text-sm text-emerald-600">
                          <Zap className="h-4 w-4" />
                          <span>ePA Enabled</span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Plans Tab */}
          <TabsContent value="plans" className="space-y-4">
            <Card>
              <CardContent className="p-0">
                <ScrollArea className="h-[500px]">
                  <Table>
                    <TableHeader className="sticky top-0 bg-background">
                      <TableRow>
                        <TableHead>Plan Name</TableHead>
                        <TableHead>Payer</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>State</TableHead>
                        <TableHead>Default</TableHead>
                        <TableHead>Rulesets</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                            Loading...
                          </TableCell>
                        </TableRow>
                      ) : filteredPlans.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                            No plans found.
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredPlans.map(plan => {
                          const planRulesets = rulesets.filter(r => r.plan_id === plan.id);
                          return (
                            <TableRow key={plan.id}>
                              <TableCell className="font-medium">{plan.plan_name}</TableCell>
                              <TableCell>{getPayerName(plan.payer_id)}</TableCell>
                              <TableCell>
                                <Badge variant="outline">
                                  {getPayerType(plan.payer_id)}
                                </Badge>
                              </TableCell>
                              <TableCell>{plan.state || 'OH'}</TableCell>
                              <TableCell>
                                {plan.is_default_plan ? (
                                  <CheckCircle className="h-4 w-4 text-emerald-500" />
                                ) : (
                                  <span className="text-muted-foreground">—</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <Badge variant="secondary">{planRulesets.length}</Badge>
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDeletePlan(plan.id)}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Rulesets Tab */}
          <TabsContent value="rulesets" className="space-y-4">
            <Card>
              <CardContent className="p-0">
                <ScrollArea className="h-[500px]">
                  <Table>
                    <TableHeader className="sticky top-0 bg-background">
                      <TableRow>
                        <TableHead>Ruleset Name</TableHead>
                        <TableHead>Payer</TableHead>
                        <TableHead>Plan</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Version</TableHead>
                        <TableHead>Confidence</TableHead>
                        <TableHead>Effective</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                            Loading...
                          </TableCell>
                        </TableRow>
                      ) : filteredRulesets.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                            No rulesets found.
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredRulesets.map(ruleset => {
                          const plan = plans.find(p => p.id === ruleset.plan_id);
                          const statusConfig = RULESET_STATUS[ruleset.status as keyof typeof RULESET_STATUS] || RULESET_STATUS.DRAFT;
                          
                          return (
                            <TableRow key={ruleset.id}>
                              <TableCell className="font-medium">
                                <div className="flex items-center gap-2">
                                  <FileStack className="h-4 w-4 text-muted-foreground" />
                                  {ruleset.name}
                                </div>
                              </TableCell>
                              <TableCell>{getPayerName(ruleset.payer_id)}</TableCell>
                              <TableCell>
                                {plan ? plan.plan_name : <span className="text-muted-foreground">All Plans</span>}
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline" className={statusConfig.color}>
                                  {statusConfig.label}
                                </Badge>
                              </TableCell>
                              <TableCell>v{ruleset.version}</TableCell>
                              <TableCell>
                                <Badge variant="outline" className={RULE_CONFIDENCE.TEMPLATE.color}>
                                  {RULE_CONFIDENCE.TEMPLATE.label}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-sm">
                                {new Date(ruleset.effective_start_date).toLocaleDateString()}
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    title="Clone ruleset"
                                    onClick={() => {
                                      setSelectedRulesetToClone(ruleset);
                                      setCloneRulesetName(`${ruleset.name} (Copy)`);
                                      setIsCloneRulesetOpen(true);
                                    }}
                                  >
                                    <Copy className="h-4 w-4" />
                                  </Button>
                                  {ruleset.status === 'DRAFT' && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      title="Activate ruleset"
                                      onClick={() => handleActivateRuleset(ruleset)}
                                    >
                                      <CheckCircle className="h-4 w-4 text-emerald-500" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Clone Ruleset Dialog */}
        <Dialog open={isCloneRulesetOpen} onOpenChange={setIsCloneRulesetOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Clone Ruleset</DialogTitle>
              <DialogDescription>
                Create a new ruleset based on an existing template.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">Cloning from:</p>
                <p className="font-medium">{selectedRulesetToClone?.name}</p>
              </div>
              <div className="space-y-2">
                <Label>New Ruleset Name</Label>
                <Input
                  placeholder="e.g., Anthem HMO Ohio 2026"
                  value={cloneRulesetName}
                  onChange={(e) => setCloneRulesetName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Assign to Plan (optional)</Label>
                <Select value={cloneRulesetPlanId} onValueChange={setCloneRulesetPlanId}>
                  <SelectTrigger>
                    <SelectValue placeholder="All plans (payer-wide)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">All plans (payer-wide)</SelectItem>
                    {plans
                      .filter(p => p.payer_id === selectedRulesetToClone?.payer_id)
                      .map(plan => (
                        <SelectItem key={plan.id} value={plan.id}>
                          {plan.plan_name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-200 rounded-lg">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <p className="text-sm text-amber-800">
                  New ruleset will be created as DRAFT. Activate when ready.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCloneRulesetOpen(false)}>Cancel</Button>
              <Button onClick={handleCloneRuleset}>Clone Ruleset</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </OpsLayout>
  );
}
